import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { formatLongDate } from '@unifemga/shared';
import { api } from '@/lib/api';
import { formatDateTime, money, paymentMethodLabel } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Modal,
  ModalHeader,
  Pagination,
  usePagedList,
} from '@/components/ui';
import { StatusBadge } from '@/components/StatusBadge';
import { DECLARATION_STATUS } from '@/lib/status';

interface Declaration {
  id: string;
  loanNumber: string;
  amount: string;
  paymentDate: string;
  paidAt: string | null;
  paymentMethod: string;
  transactionReference: string | null;
  note: string | null;
  hasReceipt: boolean;
  status: string;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
}

/**
 * Repayments the member has declared, and where each one stands.
 *
 * Separate from payment history on purpose: history is money the association
 * holds, this is money the member states they sent. Merging them would imply
 * the balance had already moved.
 */
export function MyDeclarations({ loanNumber }: { loanNumber: string }) {
  const [open, setOpen] = useState<Declaration | null>(null);

  const { data = [] } = useQuery({
    queryKey: ['my-declarations'],
    queryFn: () => api.get<Declaration[]>('/repayments/declarations/mine'),
  });

  const mine = data.filter((d) => d.loanNumber === loanNumber);
  const { rows, meta, setPage } = usePagedList(mine, 5);

  if (mine.length === 0) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Declared repayments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 pt-0">
          {rows.map((declaration) => (
            <button
              key={declaration.id}
              type="button"
              onClick={() => setOpen(declaration)}
              className="flex w-full items-center gap-3 rounded-xl border border-border px-3.5 py-2.5 text-left transition-colors hover:bg-accent"
            >
              <div className="min-w-0 flex-1">
                <p className="numeric text-sm font-semibold text-foreground">
                  {money(declaration.amount)}
                </p>
                <p className="truncate text-xs text-foreground-secondary">
                  {formatLongDate(new Date(declaration.paymentDate))}
                </p>
              </div>
              <StatusBadge map={DECLARATION_STATUS} status={declaration.status} />
              <ChevronRight size={15} className="shrink-0 text-foreground-secondary" />
            </button>
          ))}
        </CardContent>
        {meta.totalPages > 1 && (
          <Pagination meta={meta} onPageChange={setPage} className="px-5" />
        )}
      </Card>

      {open && <DeclarationModal declaration={open} onClose={() => setOpen(null)} />}
    </>
  );
}

function DeclarationModal({
  declaration,
  onClose,
}: {
  declaration: Declaration;
  onClose: () => void;
}) {
  return (
    <Modal open onClose={onClose}>
      <ModalHeader
        title={money(declaration.amount)}
        description={`Declared ${formatDateTime(declaration.createdAt)}`}
        onClose={onClose}
      />

      <div className="space-y-4 p-6">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-foreground-secondary">Status</span>
          <StatusBadge map={DECLARATION_STATUS} status={declaration.status} />
        </div>

        <dl className="space-y-2.5 border-t border-border pt-4 text-sm">
          <Row label="Loan" value={declaration.loanNumber} />
          <Row
            label="Date of payment"
            value={
              declaration.paidAt
                ? formatDateTime(declaration.paidAt)
                : formatLongDate(new Date(declaration.paymentDate))
            }
          />
          <Row label="Method" value={paymentMethodLabel(declaration.paymentMethod)} />
          {declaration.transactionReference && (
            <Row label="Reference" value={declaration.transactionReference} />
          )}
          <Row label="Receipt" value={declaration.hasReceipt ? 'Attached' : 'Not attached'} />
          {declaration.reviewedAt && (
            <Row label="Reviewed" value={formatDateTime(declaration.reviewedAt)} />
          )}
        </dl>

        {declaration.note && (
          <div className="border-t border-border pt-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-foreground-secondary">
              Your note
            </p>
            <p className="text-sm leading-relaxed text-foreground">{declaration.note}</p>
          </div>
        )}

        {declaration.reviewNote && (
          <div className="border-t border-border pt-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-foreground-secondary">
              From the reviewing officer
            </p>
            <p className="text-sm leading-relaxed text-foreground">{declaration.reviewNote}</p>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-foreground-secondary">{label}</dt>
      <dd className="numeric min-w-0 text-right font-medium text-foreground">{value}</dd>
    </div>
  );
}
